import type { WixComponentManifest } from '@wix/component-protocol/schema';
import { createComponentManifestResolver } from '@wix/component-protocol/resolver';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from './errors';
import { ManifestUrlResolver } from './ManifestUrlResolver';

export class ManifestStore {
  private _store = new Map<string, WixComponentManifest>();
  private _loadManifestsCache = new Map<
    string,
    Promise<WixComponentManifest>
  >();
  private initialLoad: null | Promise<void> = null;

  constructor(private _ManifestUrlResolver: ManifestUrlResolver) {
    this.getManifest = this.getManifest.bind(this);
    this.setManifest = this.setManifest.bind(this);
  }

  private async _loadManifestFromCDN(
    url: string,
  ): Promise<WixComponentManifest> {
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (err) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.LoadManifestFromCDNError,
        url,
      ).withParentError(err as Error);
    }
  }

  private async _loadManifest(url: string): Promise<WixComponentManifest> {
    const promiseFromCache = this._loadManifestsCache.get(url);

    if (promiseFromCache) {
      return promiseFromCache;
    }

    const loadManifestPromise = this._loadManifestFromCDN(url);
    this._loadManifestsCache.set(url, loadManifestPromise);

    loadManifestPromise.finally(() => {
      this._loadManifestsCache.delete(url);
    });

    return loadManifestPromise;
  }

  private isExternalComponent(compType: string) {
    return compType.startsWith('platform.builder');
  }

  setManifest(key: string, manifest: WixComponentManifest) {
    this._store.set(key, manifest);
  }

  setInitialLoad(promise: Promise<void>) {
    this.initialLoad = promise;
  }

  async loadManifestsByCompTypes(
    compTypes: string[],
    hardUpdate?: boolean,
  ): Promise<string[]> {
    const errors: Error[] = [];
    const compTypesToLoad = hardUpdate
      ? compTypes
      : compTypes.filter((compType) => !this._store.has(compType));

    const compTypesToReturn = await Promise.all(
      compTypesToLoad.map(async (compType) => {
        const manifestUrl =
          this._ManifestUrlResolver.getManifestUrlByCompType(compType);

        if (!manifestUrl) {
          errors.push(
            createEditorPlatformManifestServicesError(
              EditorPlatformManifestServicesErrorCode.ManifestUrlNotProvided,
              compType,
            ),
          );
          return null;
        }

        try {
          const manifest = await this._loadManifest(manifestUrl);
          this.setManifest(compType, manifest);
          return compType;
        } catch (err) {
          errors.push(err as Error);
          return null;
        }
      }),
    );

    if (errors.length > 0) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.MultipleManifestsLoadingFailed,
        'Loading manifests by comp types failed',
      ).withMultipleErrors(errors);
    }

    return compTypesToReturn.filter(Boolean) as string[];
  }

  async resolveManifestsByCompTypes(compTypes: string[]): Promise<string[]> {
    const errors: Error[] = [];
    const resolver = createComponentManifestResolver(
      this.getManifestAsync.bind(this),
    );

    await this.awaitForInitialLoad();

    await Promise.all(
      compTypes.map(async (compType) => {
        try {
          const manifest = this.getManifest(compType)!;
          const resolvedManifest = await resolver(manifest, {
            resolveElements: true,
          });
          this.setManifest(compType, resolvedManifest);
          return resolvedManifest;
        } catch (err) {
          errors.push(
            createEditorPlatformManifestServicesError(
              EditorPlatformManifestServicesErrorCode.FailedToResolveManifest,
              compType,
            )
              .withParentError(err as Error)
              .withComponentType(compType),
          );
          return null;
        }
      }),
    );

    if (errors.length > 0) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.MultipleManifestsResolvingFailed,
      ).withMultipleErrors(errors);
    }

    return compTypes;
  }

  async awaitForInitialLoad() {
    await this.initialLoad;
  }

  getManifest(key: string) {
    const manifest = this._store.get(key);

    if (manifest) {
      return manifest;
    }

    return null;
  }

  async getManifestAsync(key: string) {
    await this.awaitForInitialLoad();

    let manifest = this.getManifest(key);

    if (manifest) {
      return manifest;
    } else if (this.isExternalComponent(key)) {
      const manifestUrl =
        this._ManifestUrlResolver.getManifestUrlByCompType(key);

      if (!manifestUrl) {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ManifestUrlNotProvided,
          key,
        ).withComponentType(key);
      }

      manifest = await this._loadManifest(manifestUrl);
      this.setManifest(key, manifest);

      return manifest;
    } else {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ComponentNotExistsInEERegistry,
        'externalComponentManifest was not loaded from the EE registry and not found in the store',
      ).withComponentType(key);
    }
  }

  getManifestsByCompTypes(compTypes: string[]) {
    return compTypes.reduce((acc, compType) => {
      const manifest = this.getManifest(compType);
      if (manifest) {
        acc[compType] = manifest;
      }
      return acc;
    }, {} as Record<string, WixComponentManifest>);
  }
}
